// Sondages — card rendered inside a message + the creation modal.
// Ported from the hexagone thread; voting is a per-option toggle so rapid taps
// can't clobber each other (the server flips / moves the vote itself).

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Icon1 from "@assets/icons/Icon";

export type PollOption = { id: string; text: string; votes?: string[] };
export type PollData = {
  question: string;
  options: PollOption[];
  multi?: boolean;
  anonymous?: boolean;
  closed?: boolean;
  created_by?: string;
};

export function PollCard({
  poll,
  senderId,
  isOwn,
  isDark,
  currentUserId,
  onVote,
  onClose,
}: {
  poll: PollData;
  senderId?: string;
  isOwn?: boolean;
  isDark?: boolean;
  currentUserId: string;
  onVote: (optionId: string) => void;
  onClose: () => void;
}) {
  const options = poll.options || [];
  const closed = !!poll.closed;
  const multi = !!poll.multi;
  const totalVotes = options.reduce((sum, o) => sum + (o.votes || []).length, 0);
  const totalVoters = new Set(options.flatMap((o) => o.votes || [])).size;
  const myVotes = new Set(
    options.filter((o) => (o.votes || []).includes(currentUserId)).map((o) => o.id),
  );
  const canClose = !closed && (poll.created_by === currentUserId || senderId === currentUserId);

  const onTinted = !!(isOwn || isDark);
  const textColor = onTinted ? "#FFFFFF" : "#1E1E1E";
  const subColor = onTinted ? "rgba(255,255,255,0.6)" : "#A1A1A1";
  const fillBg = onTinted ? "rgba(255,255,255,0.28)" : "rgba(99,102,241,0.16)";
  const accent = onTinted ? "#FFFFFF" : "#6366f1";

  return (
    <div style={{ minWidth: 244, maxWidth: 300, display: "flex", flexDirection: "column", gap: 8 }}>
      <div className="flex flex-row items-center" style={{ gap: 6 }}>
        <Icon1 name="poll" size={14} color={accent} />
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: textColor, lineHeight: "18px" }}>
          {poll.question}
        </span>
      </div>
      <span style={{ fontFamily: "Poppins", fontSize: 11, color: subColor }}>
        {multi ? "Choix multiple" : "Choix unique"}
        {closed ? " · Terminé" : ""}
      </span>

      <div className="flex flex-col" style={{ gap: 6 }}>
        {options.map((o) => {
          const votes = o.votes || [];
          const pct = totalVotes ? Math.round((votes.length / totalVotes) * 100) : 0;
          const mine = myVotes.has(o.id);
          return (
            <button
              key={o.id}
              onClick={(e) => { e.stopPropagation(); if (!closed) onVote(o.id); }}
              disabled={closed}
              className="relative w-full"
              style={{
                borderRadius: 8,
                overflow: "hidden",
                border: `1px solid ${mine ? accent : onTinted ? "rgba(255,255,255,0.2)" : "#ECECEC"}`,
                background: "transparent",
                cursor: closed ? "default" : "pointer",
                padding: 0,
                textAlign: "left",
              }}
            >
              <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: `${pct}%`, background: fillBg, transition: "width 0.3s ease" }} />
              <div className="relative flex flex-row items-center" style={{ gap: 8, padding: "7px 10px" }}>
                <span
                  className="flex items-center justify-center flex-shrink-0"
                  style={{
                    width: 16, height: 16,
                    borderRadius: multi ? 4 : 8,
                    border: `1.5px solid ${mine ? accent : subColor}`,
                    background: mine ? accent : "transparent",
                  }}
                >
                  {mine && (
                    <svg width={10} height={10} viewBox="0 0 12 12" fill="none">
                      <path d="M2 6.2L4.6 8.8L10 3.4" stroke={onTinted ? "#000000" : "#FFFFFF"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span style={{ flex: 1, fontFamily: "Poppins", fontWeight: mine ? 600 : 500, fontSize: 13, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {o.text}
                </span>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: textColor, flexShrink: 0, minWidth: 30, textAlign: "right" }}>
                  {pct}%
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-row items-center justify-between">
        <span style={{ fontFamily: "Poppins", fontSize: 11, color: subColor }}>
          {totalVoters} vote{totalVoters > 1 ? "s" : ""}
        </span>
        {canClose && (
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="hover:underline"
            style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 11, color: accent, background: "none", border: "none", cursor: "pointer" }}
          >
            Clôturer
          </button>
        )}
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className="flex flex-row items-center justify-between w-full hover:bg-neutral-50"
      style={{ padding: "8px 4px", background: "none", border: "none", cursor: "pointer", borderRadius: 8 }}
    >
      <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>{label}</span>
      <span
        style={{
          width: 38, height: 22, borderRadius: 999, flexShrink: 0,
          background: checked ? "#000000" : "#D4D4D8",
          position: "relative", transition: "background 0.15s",
        }}
      >
        <span
          style={{
            position: "absolute", top: 3, left: checked ? 19 : 3,
            width: 16, height: 16, borderRadius: 999, background: "#FFFFFF",
            transition: "left 0.15s",
          }}
        />
      </span>
    </button>
  );
}

export function PollComposerModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (poll: { question: string; options: string[]; multi: boolean; anonymous: boolean }) => Promise<void> | void;
}) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<string[]>(["", ""]);
  const [multi, setMulti] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setOption = (i: number, v: string) => setOptions((prev) => prev.map((o, idx) => (idx === i ? v : o)));
  const addOption = () => setOptions((prev) => (prev.length >= 12 ? prev : [...prev, ""]));
  const removeOption = (i: number) =>
    setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i)));

  const cleanOptions = options.map((o) => o.trim()).filter(Boolean);
  const valid = !!question.trim() && cleanOptions.length >= 2;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await onCreate({ question: question.trim(), options: cleanOptions, multi, anonymous });
      onClose();
    } catch (err) {
      console.warn("[messagerie] create poll failed:", err);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.35)" }} onClick={onClose}>
      <div
        className="bg-white shadow-2xl flex flex-col overflow-hidden"
        style={{ width: 420, maxWidth: "92vw", maxHeight: "88vh", borderRadius: 16 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-row items-center justify-between border-b border-neutral-200 flex-shrink-0" style={{ padding: "12px 16px" }}>
          <span className="font-semibold" style={{ fontFamily: "Poppins", fontSize: 15 }}>Nouveau sondage</span>
          <button onClick={onClose} className="flex items-center justify-center rounded-lg hover:bg-neutral-100" style={{ width: 30, height: 30 }}>
            <Icon1 name="close" size={16} color="#525252" />
          </button>
        </div>

        <div className="flex flex-col overflow-y-auto" style={{ padding: 16, gap: 14 }}>
          <input
            autoFocus
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Posez votre question…"
            maxLength={300}
            className="w-full outline-none"
            style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 14, background: "#F5F5F5", borderRadius: 10, padding: "10px 12px" }}
          />

          <div className="flex flex-col" style={{ gap: 8 }}>
            {options.map((o, i) => (
              <div key={i} className="flex flex-row items-center" style={{ gap: 8 }}>
                <input
                  value={o}
                  onChange={(e) => setOption(i, e.target.value)}
                  placeholder={`Option ${i + 1}`}
                  maxLength={120}
                  className="flex-1 outline-none"
                  style={{ fontFamily: "Poppins", fontSize: 13, background: "#F5F5F5", borderRadius: 8, padding: "8px 12px" }}
                />
                {options.length > 2 && (
                  <button
                    onClick={() => removeOption(i)}
                    className="flex items-center justify-center hover:bg-neutral-100 rounded-md flex-shrink-0"
                    style={{ width: 28, height: 28 }}
                  >
                    <Icon1 name="close" size={12} color="#A1A1A1" />
                  </button>
                )}
              </div>
            ))}
            {options.length < 12 && (
              <button onClick={addOption} className="flex flex-row items-center hover:opacity-80" style={{ gap: 6, padding: "4px 2px" }}>
                <Icon1 name="plus" size={12} color="#6366f1" />
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#6366f1" }}>Ajouter une option</span>
              </button>
            )}
          </div>

          <div className="flex flex-col" style={{ gap: 4 }}>
            <Toggle label="Choix multiple" checked={multi} onChange={() => setMulti((v) => !v)} />
            <Toggle label="Vote anonyme" checked={anonymous} onChange={() => setAnonymous((v) => !v)} />
          </div>
        </div>

        <div className="flex flex-row justify-end border-t border-neutral-200" style={{ padding: "12px 16px", gap: 10 }}>
          <button onClick={onClose} className="hover:bg-neutral-100" style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#525252", padding: "9px 16px", borderRadius: 10 }}>
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!valid || busy}
            style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#FFFFFF", background: !valid ? "#C7C7C7" : "#000000", padding: "9px 18px", borderRadius: 10 }}
          >
            {busy ? "Envoi…" : "Créer"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
